<template>
    <div>
        <portal :to="`widget-header__${data.WidgetID}`">
            <div class="flex justify-start">
                <redesign-base-input
                    v-model="searchQuery"
                    prefix-icon="Search"
                    :placeholder="String(i18n.t('general.search'))"
                    size="mini"
                    class="mx-2"
                />
            </div>
        </portal>
        <redesign-data-table-component
            v-if="searchedTableData.length && currentColumns.length"
            :widget-data="data"
            :columns-config="currentColumnsConfig"
            :columns="currentColumns"
            :table-data="searchedTableData"
            :row-key="multiQueueElTableRowKey"
            :stripe="true"
            :style="tableStatusesColorsConfig"
            :default-available-columns="defaultVisibleColumns"
            fix-first-column
            @update-widget="updateWidget"
            @update-columns-config="updateColumnsConfig"
            @update-headers-and-columns-config="updateColumnsAndHeadersConfig"
        >
            <template #cell="{ row, column }">
                <div :style="{color: column.textColor}">
                    <div
                        v-if="column.prop === multiQueueColumnsPropNames.CALLERS || row[column.prop]?.stat === multiQueueColumnsPropNames.CALLERS"
                    >
                        <redesign-callers-count
                            :class="getCallersStatusClass(displayQueueAsRows ? row.queue_id : column.prop)"
                            :queue-id="getQueueId(row, column)"
                            :queue-name="displayQueueAsRows ? row.queue_name: row[column.prop]?.queue_name"
                            :warning-value="maximumWarningCallersValueFromSettings"
                            :filtered-queues="filteredQueues"
                            :limit-value="maximumLimitCallersValueFromSettings"
                            @update-callers-value="updateMaxCallerCount"
                            @clear-queue-max-callers="removeQueueFromMaxCallers"
                        />
                    </div>
                    <div
                        v-else-if="column.prop === multiQueueColumnsPropNames.CURRENT_WAIT_TIME || row[column.prop]?.stat === multiQueueColumnsPropNames.CURRENT_WAIT_TIME"
                    >
                        <redesign-max-wait-time
                            :class="getWaitTimeStatusClass(displayQueueAsRows ? row.queue_id: column.prop)"
                            :queue-id="getQueueId(row, column)"
                            :queue-name="displayQueueAsRows ? row.queue_name: row[column.prop]?.queue_name"
                            :limit-value="maximumLimitWaitTimeValueFromSettings"
                            :warning-value="maximumWarningWaitTimeValueFromSettings"
                            :filtered-queues="filteredQueues"
                            @update-wait-time-value="updateCurrentMaxWaitTime"
                            @clear-calls="removeQueueFromMaxWaitTime"
                        />
                    </div>
                    <div
                        v-else-if="column.prop === multiQueueColumnsPropNames.ACTIVE_QUEUE_CALLS || row[column.prop]?.stat === multiQueueColumnsPropNames.ACTIVE_QUEUE_CALLS"
                    >
                        <redesign-active-queue-calls
                            :queue-id="getQueueId(row, column)"
                            :table-data="tableDataMergedWithRequest"
                        />
                    </div>
                    <RedesignAgentsCountCell
                        v-else-if="column.prop === multiQueueColumnsPropNames.AGENTS_ASSIGNED_AND_AVAILABLE || row[column.prop]?.stat === multiQueueColumnsPropNames.AGENTS_ASSIGNED_AND_AVAILABLE"
                        :queue-id="getQueueId(row, column)"
                        :available="true"
                        :agents="agents"
                    />
                    <RedesignAgentsCountCell
                        v-else-if="column.prop === multiQueueColumnsPropNames.AGENTS_ASSIGNED_AND_UNAVAILABLE || row[column.prop]?.stat === multiQueueColumnsPropNames.AGENTS_ASSIGNED_AND_UNAVAILABLE"
                        :queue-id="getQueueId(row, column)"
                        :available="false"
                        :agents="agents"
                    />
                    <span v-else-if="!displayQueueAsRows">
                        <span
                            v-if="row[column.prop]"
                            :style="{color: row[column.prop].textColor}"
                        >
                            {{ row[column.prop].value }}
                        </span>
                    </span>
                    <span v-else>
                        {{ isTimeProp(column.prop) ? timeFormatter(row[column.prop]) : row[column.prop] }}
                    </span>
                </div>
            </template>
        </redesign-data-table-component>
    </div>
</template>

<script setup lang="ts">
import { computed, defineEmits, onBeforeUnmount, ref, toRefs, watch } from 'vue'
import { getOptionsList } from '@/helpers/entitiesList'
import { timeFormatter } from '@/helpers/timeFormatter'
import { ADDITIONAL_DATA_KEY, ALL_QUEUE_ID } from '@/enum/redesignQueueDashboardStatistics'
import i18n from '@/i18n'
import {
    removeSoundsFromQueueByIdWithHistoryKeep,
    updateSoundCurrentValue,
    updateTimerStateValue
} from '@/composables/useSoundsStoreData'
import {
    billingCdrQueueTypes,
    defaultAvailableColumns,
    defaultRowData,
    DefaultRowData,
    defaultVisibleColumns,
    ExitCount,
    FilteredMultiQueueColumn,
    MaxCallerCount,
    MaxCallersCount,
    MaxWaitTime,
    MaxWaitTimes,
    multiQueueColumnsPropNames,
    queueDashboardDefaultColumnStyles,
    QueueData,
    QueueFromEntities
} from '@/helpers/multiQueueDashboardRedesign'
import { sortTableDataByDefaultList } from '@/helpers/tableSortingUtils'
import {
    getMultiQueueElTableRowKeyAsColumns,
    getMultiQueueElTableRowKeyAsRows,
    resetCallersThresholdTracking,
    resetWaitTimeThresholdTracking,
} from '@/composables/useMultiQueueTableCommon'
import { useQueuesThresholdNotifications } from '@/composables/useQueuesThresholdNotifications'
import RedesignDataTableComponent from '@/components/RedesignBaseComponents/RedesignDataTableComponent.vue'
import RedesignCallersCount from '@/components/RedesignComponents/Queue/RedesignCallersCount.vue'
import RedesignMaxWaitTime from '@/components/RedesignComponents/Queue/RedesignMaxWaitTime.vue'
import { ColumnsObjectConfig, TableHeadersConfig, WidgetExtended } from '@/types/dashboard'
import { hasColumnSpecificAlignment } from '@/helpers/alignmentUtils'
import { limitThresholdValue, warningThresholdValue } from '@/composables/useSoundsThresholdNotification'
import RedesignBaseInput from '@/components/RedesignBaseComponents/Inputs/RedesignBaseInput.vue'
import { getMinValue } from '@/helpers/util'
import { getInitialTime } from '@/util/timeUtils'
import { activeCallsQueueMap } from '@/composables/useSharedQueueCallsData'
import useTableHeaderTranslations from '@/composables/useTableHeaderTranslations'
import RedesignActiveQueueCalls from '@/components/RedesignComponents/Queue/RedesignActiveQueueCalls.vue'
import RedesignAgentsCountCell from '@/components/RedesignComponents/Queue/RedesignAgentsCountCell.vue'
import { useQueueAgentsInline } from '@/composables/useQueueAgentsInline'
import useEntityPositiveNegativeFilters from '@/composables/useEntityPositiveNegativeFilters'
import { getTranslationOrUndefined } from '@/helpers/getTranslation'
import startCase from 'lodash/startCase'

const QUEUE_LIST_KEY = '{|queue_list|}'
const warningStatus = 'warning'
const limitStatus = 'limit'
const idleStatus = 'idle'
const callersBaseNotificationId = 'callers'
const waitTimeNotificationId = 'wait_time'
const waitTimeWarningProperty = 'max warning wait time'
const waitTimeLimitProperty = 'max limit wait time'
const callersWarningProperty = 'max warning callers'
const callersLimitProperty = 'max limit callers'
const notificationTag = 'widget.redesignMultiQueueTableHtml'

const emit = defineEmits<{
    (event: 'update-widget', payload: WidgetExtended): void
}>()

const props = defineProps<{
    data: WidgetExtended
    queueTableData: QueueData[]
}>()

/*Data*/
const maxCurrentQueuesCallerCount = ref<MaxCallersCount>({})
const maxCurrentQueuesWaitingTime = ref<MaxWaitTimes>({})
const allEntityQueues = ref<QueueFromEntities[] | []>([])
const columnsObjectConfig = ref({})
const columnsAsQueuesConfig = ref({})
const callersStatus = ref('')
const waitingQueueStatus = ref('')
const { data: widgetData } = toRefs(props)
const searchQuery = ref('')

const {
    maximumWarningCallersValueFromSettings,
    maximumLimitCallersValueFromSettings,
    maximumWarningWaitTimeValueFromSettings,
    maximumLimitWaitTimeValueFromSettings,
    maximumWarningCallersSound,
    maximumLimitCallersSound,
    maximumWarningMaxRingTimeSound,
    maximumLimitMaxRingTimeSound,
    setupNotification
} = useQueuesThresholdNotifications(widgetData)

const { makeTableHeaderTranslations, makeTableHeaderTooltipTranslations } = useTableHeaderTranslations()

const { getAgents } = useQueueAgentsInline()

const { filteredItems: filteredQueues } = useEntityPositiveNegativeFilters(widgetData, QUEUE_LIST_KEY, allEntityQueues, 'queue_id')
//Computed

const agents = computed(() => {
    return getAgents() || {
        allAvailable: [],
        allUnavailable: []
    }
})

const displayRowWithTotal = computed(() => {
    return props.data.WidgetLayout?.displayRowWithTotals ?? true
})
const displayQueueAsRows = computed(() => {
    return props.data.WidgetLayout?.displayQueuesAsRow ?? true
})
const showStatsInPercentage = computed(() => {
    return props.data.WidgetLayout?.showStatsInPercentage ?? false
})

const columns = computed(() =>
    defaultAvailableColumns.map((column) => {
        const currentColumn = props.data?.WidgetTemplateColumns?.find(
            (col) => column.prop === col.WidgetTemplateColumnsName
        )

        if (!currentColumn) return column

        const label = getTranslationOrUndefined(currentColumn.TemplateNameContentTag)
            || getTranslationOrUndefined(currentColumn.WidgetTemplateColumnsName)
            || startCase(currentColumn.WidgetTemplateColumnsName)

        if (label) {
            column.label = label
        }

        return column
    })
)
const filteredColumns = computed<FilteredMultiQueueColumn[]>(() =>
    columns.value.map((column) => ({
        ...column,
        label: makeTableHeaderTranslations(column, column.label),
        tooltipLabel: makeTableHeaderTooltipTranslations(column, column.label),
        textColor: getColumnTextColor(column.prop),
    }))
)
const currentColumns = computed(() => {
    if (displayQueueAsRows.value) {
        return filteredColumns.value
    }
    if (!displayQueueAsRows.value) {
        return oposideColumns.value
    }
    return []
})
const currentTableData = computed(() => {
    if (displayQueueAsRows.value) {
        return tableDataMergedWithRequest.value || []
    }
    return mappedOppTableData.value || []
})

const maximumWarningCallersColor = computed(() => props.data?.WidgetLayout?.maximumWarningCallersColor || '')
const maximumLimitCallersColor = computed(() => props.data?.WidgetLayout?.maximumLimitCallersColor || '')
const maximumWarningMaxRingTimeColor = computed(() => props.data?.WidgetLayout?.maximumWarningMaxRingTimeColor || '')
const maximumLimitMaxRingTimeColor = computed(() => props.data?.WidgetLayout?.maximumLimitMaxRingTimeColor || '')
const tableStatusesColorsConfig = computed(() => {
    return {
        '--maximum-warning-callers-color': maximumWarningCallersColor.value,
        '--maximum-limit-callers-color': maximumLimitCallersColor.value,
        '--maximum-warning-max-ring-time-color': maximumWarningMaxRingTimeColor.value,
        '--maximum-limit-max-ring-time-color': maximumLimitMaxRingTimeColor.value

    }
})
const mappedBaseTableData = computed<Record<number | string, DefaultRowData>>(() => {
    if (!filteredQueues.value || !filteredQueues.value.length) {
        return {}
    }
    const baseDataObject: Record<number, DefaultRowData> = {}
    filteredQueues.value.forEach((queue: QueueFromEntities) => {
        baseDataObject[queue.queue_id] = {
            ...defaultRowData,
            queue_id: queue.queue_id,
            queue_name: queue.q_name,
        }
    })
    return baseDataObject
})
const searchedTableData = computed(() => {
    if (!currentTableData.value.length) {
        return currentTableData.value
    }
    const query = searchQuery.value.trim().toLowerCase()
    if (!query) return currentTableData.value

    return currentTableData.value.filter(item =>
        Object.values(item).some(val =>
            String(val).toLowerCase().includes(query)
        )
    )
})
const queueTableDataById = computed<Map<number | string, QueueData>>(() => {
    const map = new Map<number | string, QueueData>()
    for (const item of props.queueTableData) {
        map.set(item.queue_id, item)
    }
    return map
})

const tableDataMergedWithRequest = computed(() => {
    const maxRingTime: number[] = []
    const averageRingTime: number[] = []
    let totalCallsCount = 0
    const totalData: Record<string, string | number> = {
        ...defaultRowData,
        queue_id: ALL_QUEUE_ID,
        queue_name: ALL_QUEUE_ID
    }
    const statsById = queueTableDataById.value
    const callerCountByQueue = maxCurrentQueuesCallerCount.value
    const activeCallsByQueue = activeCallsQueueMap.value

    filteredQueues.value.forEach((queue) => {
        const row = mappedBaseTableData.value[queue.queue_id]
        const calls = activeCallsByQueue.get(queue.queue_id)?.Calls ?? []

        row[multiQueueColumnsPropNames.CALLERS] = calls.length
        row[multiQueueColumnsPropNames.CURRENT_WAIT_TIME] = getInitialTime(
            calls.length
                ? getMinValue(calls, 'JoinTimeStamp_UTC_CLIENT')
                : new Date().getTime() * 10000
        )
        row[multiQueueColumnsPropNames.CALL_COUNT] = callerCountByQueue[queue.queue_id]?.value ?? 0

        const queueData = statsById.get(queue.queue_id)

        if (!queueData) {
            return
        }
        const CallCount = queueData[multiQueueColumnsPropNames.CALL_COUNT]

        if (typeof CallCount === 'number') {
            totalCallsCount += CallCount
        }

        for (const key in queueData) {

            const value = queueData[key]

            if (Array.isArray(value) && key === ADDITIONAL_DATA_KEY && value.length) {
                (value as ExitCount[]).forEach((stat) => {
                    const keyName = billingCdrQueueTypes[stat.billing_cdr_queue_type]

                    if (showStatsInPercentage.value && typeof CallCount === 'number') {
                        mappedBaseTableData.value[queueData.queue_id][keyName] = parseFloat((stat.ExitTypeCount / CallCount * 100).toFixed(2)) + '%'
                    } else {
                        mappedBaseTableData.value[queueData.queue_id][keyName] = stat.ExitTypeCount
                    }

                    if (typeof totalData[keyName] !== 'number') {
                        totalData[keyName] = 0
                    }

                    totalData[keyName] = totalData[keyName] + stat.ExitTypeCount
                })
            } else if (!Array.isArray(value) && key !== 'queue_id') {
                if (key === multiQueueColumnsPropNames.MAX_RING_TIME && typeof value === 'number') {
                    mappedBaseTableData.value[queueData.queue_id][key] = value
                    maxRingTime.push(value)
                } else if (key === multiQueueColumnsPropNames.AVG_RING_TIME && typeof value === 'number' && value && typeof CallCount === 'number') {
                    mappedBaseTableData.value[queueData.queue_id][key] = value
                    averageRingTime.push(value * CallCount)
                } else if (showStatsInPercentage.value && typeof CallCount === 'number' && typeof value === 'number' && key !== multiQueueColumnsPropNames.CALL_COUNT && key !== multiQueueColumnsPropNames.AVG_ANSWER_HOURLY) {
                    mappedBaseTableData.value[queueData.queue_id][key] = parseFloat((value / CallCount * 100).toFixed(2)) + '%'
                } else {
                    mappedBaseTableData.value[queueData.queue_id][key] = value
                }

                if (typeof value === 'number' && typeof totalData[key] === 'number') {
                    totalData[key] += value
                }
            }
        }
    })
    if (maxRingTime.length) {
        totalData[multiQueueColumnsPropNames.MAX_RING_TIME] = Math.max(...maxRingTime)
    }

    if (averageRingTime.length) {
        const totalRingTime = averageRingTime.reduce((acc, value) => {
            acc += value
            return acc

        }, 0)
        totalData[multiQueueColumnsPropNames.AVG_RING_TIME] = Math.ceil(totalRingTime / totalCallsCount)
    }
    totalData[multiQueueColumnsPropNames.CALL_COUNT] = totalCallsCount

    // Fix floating point precision for AvgAnswerHourly
    const avgAnswerHourly = totalData[multiQueueColumnsPropNames.AVG_ANSWER_HOURLY]
    if (typeof avgAnswerHourly === 'number') {
        totalData[multiQueueColumnsPropNames.AVG_ANSWER_HOURLY] = parseFloat(avgAnswerHourly.toFixed(2))
    }

    // Fix floating point precision for AnsweredWithCBPercent
    const answeredWithCBPercent = totalData[multiQueueColumnsPropNames.ANSWERED_WITH_CB_PERCENT]
    if (typeof answeredWithCBPercent === 'number') {
        totalData[multiQueueColumnsPropNames.ANSWERED_WITH_CB_PERCENT] = parseFloat(answeredWithCBPercent.toFixed(2))
    }

    const allData = Object.values(mappedBaseTableData.value)

    if (displayRowWithTotal.value) {
        allData.push(totalData)
    }
    return sortTableDataByDefaultList(allData, props.data, 'queue_id')
})
// Get all available stat types from defaultRowData
const availableStatTypes = computed(() =>
    Object.keys(defaultRowData).filter(
        key => key !== multiQueueColumnsPropNames.QUEUE_NAME && key !== 'queue_id'
    )
)

const oposideColumns = computed(() => {
    const queueColumns = filteredQueues.value.map(queue => ({
        prop: String(queue.queue_id),
        label: {
            shortTextLabel: queue.q_name,
            fullTextLabel: queue.q_name,
        },
        tooltipLabel: queue.q_name,
        textColor: getColumnTextColor(queue.queue_id),
    }))
    const columns = [
        {
            prop: 'Statistic',
            label: {
                shortTextLabel: 'Stat Type',
                fullTextLabel: 'Stat Type'

            },
            tooltipLabel: 'Stat Type',
            filterableConfig: {
                componentProps: {
                    filterOptions: availableStatTypes.value
                }
            }
        },
        ...queueColumns,
    ]
    if (displayRowWithTotal.value) {
        columns.push({
            prop: ALL_QUEUE_ID,
            label: {
                shortTextLabel: ALL_QUEUE_ID,
                fullTextLabel: ALL_QUEUE_ID
            },
            tooltipLabel: ALL_QUEUE_ID
        },)
    }
    return columns
})

const mappedOppTableData = computed(() => {
    const statistics = availableStatTypes.value

    if (!tableDataMergedWithRequest.value || !tableDataMergedWithRequest.value.length) {
        return []
    }
    return statistics.map(stat => {
        return tableDataMergedWithRequest.value.reduce((row, queue) => {
            const key = String(queue.queue_id)
            row[key] = {
                value: queue[stat],
                textColor: getColumnTextColor(stat),
                stat,
                queue_name: queue.queue_name
            }
            return row
        }, {
            Statistic: {
                value: stat,
                textColor: getColumnTextColor(stat)
            }
        })
    })
})

const queuesMaxCallers = computed(() => {
    const callersArray = Object.entries(maxCurrentQueuesCallerCount.value)
    if (!callersArray.length) {
        return null
    }
    const maxValue = callersArray.sort((a, b) => a[1].value - b[1].value).pop()

    if (!maxValue) {
        return null
    }

    return {
        queueId: maxValue[0],
        ...maxValue[1]
    }
})
const queuesMaxWaiting = computed(() => {
    const waitingTimeArray = Object.entries(maxCurrentQueuesWaitingTime.value)
    if (!waitingTimeArray.length) {
        return null
    }
    const maxValue = waitingTimeArray.sort((a, b) => b[1].value - a[1].value).pop()
    if (maxValue) {
        return {
            queueId: maxValue[0],
            ...maxValue[1]
        }
    }
    return null
})
const currentColumnsConfig = computed(() => {
    if (displayQueueAsRows.value) {
        return columnsAsQueuesConfig.value
    }
    return columnsObjectConfig.value
})

/*Methods*/
const getColumnTextColor = (prop: string | number) => {
    const color = queueDashboardDefaultColumnStyles[prop]
    if (color) {
        return color.color
    }
    return ''
}
const getQueueId = (row: any, column: any): number | string => {
    if (displayQueueAsRows.value) {
        return row.queue_id
    }
    return column.prop === ALL_QUEUE_ID ? ALL_QUEUE_ID : Number(column.prop)
}
const updateMaxCallerCount = ({ queueId, value, queueName, thresholdStatus }: MaxCallerCount) => {
    const key = typeof queueId === 'string' ? Number(queueId) : queueId
    if (isNaN(key)) return
    maxCurrentQueuesCallerCount.value =
        {
            ...maxCurrentQueuesCallerCount.value,
            [key]: {
                value,
                queueName,
                thresholdStatus
            }
        }
}
const removeQueueFromMaxCallers = (id: number | string) => {
    const key = typeof id === 'string' ? Number(id) : id
    if (isNaN(key)) return
    delete maxCurrentQueuesCallerCount.value[key]
    maxCurrentQueuesCallerCount.value = { ...maxCurrentQueuesCallerCount.value }
}
const updateCurrentMaxWaitTime = ({ queueId, value, thresholdStatus, queueName }: MaxWaitTime) => {
    const key = typeof queueId === 'string' ? Number(queueId) : queueId
    if (isNaN(key)) return
    maxCurrentQueuesWaitingTime.value =
        {
            ...maxCurrentQueuesWaitingTime.value,
            [key]: {
                value,
                queueName,
                thresholdStatus
            }
        }
}
const updateCurrentNotification = (id: string, queueName: string, value: number, type: string) => {
    const notificationData = {
        notification_value: type === limitThresholdValue ? maximumLimitCallersValueFromSettings.value : maximumWarningCallersValueFromSettings.value,
        entity_name: queueName,
        current_value: `${value}.`,
        property: type === limitThresholdValue ? callersLimitProperty : callersWarningProperty
    }

    const notificationHtml = i18n.t(notificationTag, { ...notificationData })
    updateSoundCurrentValue(id, notificationHtml)

}
const removeQueueFromMaxWaitTime = (id: number | string) => {
    const key = typeof id === 'string' ? Number(id) : id
    if (isNaN(key)) return
    delete maxCurrentQueuesWaitingTime.value[key]
    maxCurrentQueuesWaitingTime.value = { ...maxCurrentQueuesWaitingTime.value }
}
const fetchEntityQueues = async () => {
    allEntityQueues.value = await getOptionsList(QUEUE_LIST_KEY)
}
const getCallersStatusClass = (id: number | string): string => {
    const key = typeof id === 'string' ? Number(id) : id
    if (isNaN(key)) return ''
    return maxCurrentQueuesCallerCount.value?.[key]?.thresholdStatus
        ? `is--callers-status-${maxCurrentQueuesCallerCount.value[key].thresholdStatus}`
        : ''
}
const getWaitTimeStatusClass = (id: number | string): string => {
    const key = typeof id === 'string' ? Number(id) : id
    if (isNaN(key)) return ''
    return maxCurrentQueuesWaitingTime.value?.[key]?.thresholdStatus
        ? `is--waiting-status-${maxCurrentQueuesWaitingTime.value[key].thresholdStatus}`
        : ''
}
const isTimeProp = (prop: string) => {
    return prop === multiQueueColumnsPropNames.MAX_RING_TIME || prop === multiQueueColumnsPropNames.AVG_RING_TIME
}
const updateColumnsConfig = (config: ColumnsObjectConfig) => {
    if (!config) {
        return
    }
    setColumnsConfig(config)

    const hasColumnSpecificAlignmentValue = hasColumnSpecificAlignment(config, props.data.WidgetLayout?.tableColumnsAlignment)

    const updatedWidgetData = {
        ...props.data,
        WidgetLayout: {
            ...(props.data.WidgetLayout || {}),
            hasColumnSpecificAlignment: hasColumnSpecificAlignmentValue,
            ...(displayQueueAsRows.value
                ? { columnsAsQueuesConfig: config }
                : { columnsObjectConfig: config })
        }
    }
    emit('update-widget', updatedWidgetData)
}
const updateColumnsAndHeadersConfig = (config: ColumnsObjectConfig, tableHeadersConfig: TableHeadersConfig) => {
    if (config) {
        setColumnsConfig(config)
    }

    const hasColumnSpecificAlignmentValue = hasColumnSpecificAlignment(config, props.data.WidgetLayout?.tableColumnsAlignment)

    const updatedWidgetData = {
        ...props.data,
        WidgetLayout: {
            ...(props.data.WidgetLayout || {}),
            hasColumnSpecificAlignment: hasColumnSpecificAlignmentValue,
            ...(config
                ? (displayQueueAsRows.value
                    ? { columnsAsQueuesConfig: config }
                    : { columnsObjectConfig: config })
                : {}),
            ...(tableHeadersConfig ? { tableHeadersConfig } : {})
        }
    }
    updateWidget(updatedWidgetData)
}
const setColumnsConfig = (config: ColumnsObjectConfig) => {
    if (displayQueueAsRows.value) {
        columnsAsQueuesConfig.value = config
    } else {
        columnsObjectConfig.value = config
    }
}
const multiQueueElTableRowKey = (row: Record<string, unknown>): string => {
    return displayQueueAsRows.value 
        ? getMultiQueueElTableRowKeyAsRows(row)
        : getMultiQueueElTableRowKeyAsColumns(row)
}
const updateWidget = (newData: WidgetExtended) => {
    emit('update-widget', newData)
}

/*  Watchers */
watch(() => props.data.WidgetLayout, (val) => {
    if (val) {
        columnsObjectConfig.value = val?.columnsObjectConfig ?? {}
        columnsAsQueuesConfig.value = val?.columnsAsQueuesConfig ?? {}
    }
}, {
    immediate: true,
})

watch(
    () => [maximumWarningCallersValueFromSettings.value, maximumLimitCallersValueFromSettings.value],
    ([warn, limit]) => {
        if (!warn && !limit) {
            resetCallersThresholdTracking(maxCurrentQueuesCallerCount, callersStatus)
        }
    }
)

watch(() => queuesMaxCallers.value, (newValue, oldValue) => {
    if (!maximumWarningCallersValueFromSettings.value && !maximumLimitCallersValueFromSettings.value) {
        resetCallersThresholdTracking(maxCurrentQueuesCallerCount, callersStatus)
        return
    }

    if (!newValue && oldValue) {
        removeSoundsFromQueueByIdWithHistoryKeep(`${callersBaseNotificationId}${oldValue.queueId}`)
        callersStatus.value = idleStatus
        return
    }

    if (!newValue) {
        return
    }

    const newId = `${callersBaseNotificationId}${newValue.queueId}`
    let oldId = ''

    if (oldValue) {
        oldId = `${callersBaseNotificationId}${oldValue.queueId}`
    }

    if (newId === oldId && newValue.thresholdStatus === callersStatus.value) {
        updateCurrentNotification(newId, newValue.queueName, newValue.value, newValue.thresholdStatus)

        return
    } else if (oldId && newId !== oldId || newValue.thresholdStatus !== callersStatus.value) {
        removeSoundsFromQueueByIdWithHistoryKeep(oldId)
    }

    let playedSound = false
    if (newValue.thresholdStatus === callersStatus.value || (callersStatus.value === limitStatus && newValue.thresholdStatus === warningStatus)) {
        playedSound = true
    }
    if (newValue.value) {
        callersStatus.value = newValue.thresholdStatus
        setupNotification({
            thresholdValue: callersStatus.value === limitThresholdValue ? maximumLimitCallersValueFromSettings.value : maximumWarningCallersValueFromSettings.value,
            entityName: newValue.queueName,
            soundPlayed: playedSound,
            type: callersStatus.value,
            id: newId,
            currentValue: newValue.value,
            notificationTag,
            property: callersStatus.value === limitThresholdValue ? callersLimitProperty : callersWarningProperty
        })
    }

}, {
    immediate: true
})

watch(
    () => [maximumWarningCallersSound.value, maximumLimitCallersSound.value],
    ([newWarningSound, newLimitSound], [oldWarningSound, oldLimitSound]) => {
        const currentCallers = queuesMaxCallers.value
        if (!currentCallers) return

        const isActiveSoundChanged =
            (currentCallers.thresholdStatus === warningThresholdValue && newWarningSound !== oldWarningSound) ||
            (currentCallers.thresholdStatus === limitThresholdValue && newLimitSound !== oldLimitSound)
        if (!isActiveSoundChanged) return

        const currentId = `${callersBaseNotificationId}${currentCallers.queueId}`
        removeSoundsFromQueueByIdWithHistoryKeep(currentId)

        setupNotification({
            thresholdValue: currentCallers.thresholdStatus === limitThresholdValue ? maximumLimitCallersValueFromSettings.value : maximumWarningCallersValueFromSettings.value,
            entityName: currentCallers.queueName,
            soundPlayed: true,
            type: currentCallers.thresholdStatus,
            id: currentId,
            currentValue: currentCallers.value,
            notificationTag,
            property: currentCallers.thresholdStatus === limitThresholdValue ? callersLimitProperty : callersWarningProperty
        })
    }
)

watch(
    () => [maximumWarningWaitTimeValueFromSettings.value, maximumLimitWaitTimeValueFromSettings.value],
    ([warn, limit]) => {
        if (!warn && !limit) {
            resetWaitTimeThresholdTracking(maxCurrentQueuesWaitingTime, waitingQueueStatus)
        }
    }
)

watch(() => queuesMaxWaiting.value, (newValue, oldValue) => {
    if (!newValue && !oldValue) return

    if (!maximumWarningWaitTimeValueFromSettings.value && !maximumLimitWaitTimeValueFromSettings.value) {
        resetWaitTimeThresholdTracking(maxCurrentQueuesWaitingTime, waitingQueueStatus)
        return
    }

    const newId = newValue ? `${waitTimeNotificationId}${newValue.queueId}` : ''
    const oldId = oldValue ? `${waitTimeNotificationId}${oldValue.queueId}` : ''

    if (!newValue) {
        removeSoundsFromQueueByIdWithHistoryKeep(oldId)
        waitingQueueStatus.value = idleStatus
        return
    }

    const sameIdAndStatus = newId === oldId && newValue.thresholdStatus === waitingQueueStatus.value
    if (sameIdAndStatus && newValue.value === oldValue?.value) return

    // If the ID matches and the status is unchanged but the value is different, update the timer state
    if (sameIdAndStatus && newValue.value !== oldValue?.value) {
        updateTimerStateValue(newId, newValue.value)
        return
    }

    // Remove old notification if the ID or status has changed
    if (oldId && (newId !== oldId || newValue.thresholdStatus !== waitingQueueStatus.value)) {
        removeSoundsFromQueueByIdWithHistoryKeep(oldId)
    }

    const soundPlayed = newValue.thresholdStatus === waitingQueueStatus.value || (newValue.thresholdStatus === warningThresholdValue && waitingQueueStatus.value === limitThresholdValue)
    if (newValue.value) {
        waitingQueueStatus.value = newValue.thresholdStatus

        setupNotification({
            thresholdValue: timeFormatter(
                waitingQueueStatus.value === limitThresholdValue
                    ? maximumLimitWaitTimeValueFromSettings.value
                    : maximumWarningWaitTimeValueFromSettings.value
            ),
            entityName: newValue.queueName,
            soundPlayed,
            type: waitingQueueStatus.value,
            id: newId,
            timerStartValue: newValue.value,
            currentValue: '',
            notificationTag,
            property: waitingQueueStatus.value === limitThresholdValue
                ? waitTimeLimitProperty
                : waitTimeWarningProperty
        })
    }
}, {
    immediate: true
})

watch(
    () => [maximumWarningMaxRingTimeSound.value, maximumLimitMaxRingTimeSound.value],
    ([newWarningSound, newLimitSound], [oldWarningSound, oldLimitSound]) => {
        const currentWaiting = queuesMaxWaiting.value
        if (!currentWaiting) return

        const isActiveSoundChanged =
            (currentWaiting.thresholdStatus === warningThresholdValue && newWarningSound !== oldWarningSound) ||
            (currentWaiting.thresholdStatus === limitThresholdValue && newLimitSound !== oldLimitSound)
        if (!isActiveSoundChanged) return

        const currentId = `${waitTimeNotificationId}${currentWaiting.queueId}`
        removeSoundsFromQueueByIdWithHistoryKeep(currentId)

        setupNotification({
            thresholdValue: timeFormatter(
                currentWaiting.thresholdStatus === limitThresholdValue
                    ? maximumLimitWaitTimeValueFromSettings.value
                    : maximumWarningWaitTimeValueFromSettings.value
            ),
            entityName: currentWaiting.queueName,
            soundPlayed: true,
            type: currentWaiting.thresholdStatus,
            id: currentId,
            timerStartValue: currentWaiting.value,
            currentValue: '',
            notificationTag,
            property: currentWaiting.thresholdStatus === limitThresholdValue
                ? waitTimeLimitProperty
                : waitTimeWarningProperty
        })
    }
)
fetchEntityQueues()

onBeforeUnmount(() => {
    const callers = queuesMaxCallers.value
    if (callers) {
        removeSoundsFromQueueByIdWithHistoryKeep(`${callersBaseNotificationId}${callers.queueId}`)
    }
    const waiting = queuesMaxWaiting.value
    if (waiting) {
        removeSoundsFromQueueByIdWithHistoryKeep(`${waitTimeNotificationId}${waiting.queueId}`)
    }
})
</script>

<style scoped lang="scss">

</style>
